import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Switch,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  getAdminFeatureFlags,
  createFeatureFlag,
  updateFeatureFlag,
  deleteFeatureFlag,
} from "../../../utility/httpActions/featureFlagActions";

const FeatureFlagList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteKey, setDeleteKey] = useState(null);
  const [newFlag, setNewFlag] = useState({
    key: "",
    description: "",
    enabled: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["admin-feature-flags"],
    queryFn: getAdminFeatureFlags,
  });

  const invalidateFlags = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-feature-flags"] });
    queryClient.invalidateQueries({ queryKey: ["featureFlags"] });
  };

  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }) => updateFeatureFlag(key, { enabled }),
    onSuccess: () => invalidateFlags(),
    onError: () =>
      setSnackbar({
        open: true,
        message: "Failed to update flag",
        severity: "error",
      }),
  });

  const createMutation = useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: () => {
      invalidateFlags();
      setCreateOpen(false);
      setNewFlag({ key: "", description: "", enabled: false });
      setSnackbar({ open: true, message: "Flag created", severity: "success" });
    },
    onError: (err) =>
      setSnackbar({
        open: true,
        message: err?.data?.message || "Failed to create flag",
        severity: "error",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFeatureFlag,
    onSuccess: () => {
      invalidateFlags();
      setDeleteKey(null);
      setSnackbar({ open: true, message: "Flag deleted", severity: "success" });
    },
    onError: () =>
      setSnackbar({
        open: true,
        message: "Failed to delete flag",
        severity: "error",
      }),
  });

  if (isLoading) return <CircularProgress />;

  const flags = data?.flags || [];

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Feature Flags
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Flag
        </Button>
      </Stack>

      {flags.length === 0 ? (
        <Typography color="text.secondary" mt={4} textAlign="center">
          No feature flags yet. Create one to get started.
        </Typography>
      ) : isMobile ? (
        <Stack spacing={2}>
          {flags.map((f) => (
            <Card key={f.key} variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={600} fontFamily="monospace">
                        {f.key}
                      </Typography>
                      {!f.existsInDb && (
                        <Chip label="Not created" size="small" variant="outlined" />
                      )}
                    </Stack>
                    {f.description && (
                      <Typography variant="body2" color="text.secondary">
                        {f.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {f.existsInDb
                        ? `Updated ${new Date(f.updatedAt).toLocaleDateString()}`
                        : "Defaults to disabled"}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center">
                    <Switch
                      checked={f.enabled}
                      onChange={() =>
                        toggleMutation.mutate({
                          key: f.key,
                          enabled: !f.enabled,
                        })
                      }
                      color="primary"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      disabled={!f.existsInDb}
                      onClick={() => setDeleteKey(f.key)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {flags.map((f) => (
              <TableRow key={f.key}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontFamily="monospace" fontSize={14}>
                      {f.key}
                    </Typography>
                    {!f.existsInDb && (
                      <Chip label="Not created" size="small" variant="outlined" />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{f.description || "—"}</TableCell>
                <TableCell>
                  <Switch
                    checked={f.enabled}
                    onChange={() =>
                      toggleMutation.mutate({ key: f.key, enabled: !f.enabled })
                    }
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  {f.existsInDb
                    ? new Date(f.updatedAt).toLocaleDateString()
                    : "—"}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!f.existsInDb}
                    onClick={() => setDeleteKey(f.key)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Feature Flag</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Key"
              placeholder="my-feature"
              helperText="Lowercase letters, numbers, and hyphens"
              value={newFlag.key}
              onChange={(e) =>
                setNewFlag((p) => ({ ...p, key: e.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              label="Description"
              placeholder="What this flag controls"
              value={newFlag.description}
              onChange={(e) =>
                setNewFlag((p) => ({ ...p, description: e.target.value }))
              }
              fullWidth
              multiline
              rows={2}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                checked={newFlag.enabled}
                onChange={(e) =>
                  setNewFlag((p) => ({ ...p, enabled: e.target.checked }))
                }
              />
              <Typography>
                {newFlag.enabled ? "Enabled" : "Disabled"}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate(newFlag)}
            disabled={!newFlag.key || createMutation.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteKey} onClose={() => setDeleteKey(null)}>
        <DialogTitle>Delete Feature Flag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteKey}</strong>? Any
            code checking this flag will fall back to disabled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteKey(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteKey)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeatureFlagList;
